/**
 * Organization Management Page
 * Admin-only page for managing organizations
 */

import React, { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import {
	useDeleteOrganizationMutation,
	useListOrganizationsQuery,
	type Organization,
} from "../../store/api/organizationsApi";
import CreateOrganizationModal from "./components/CreateOrganizationModal";
import OrganizationFilters from "./components/OrganizationFilters";
import OrganizationTable from "./components/OrganizationTable";

const OrganizationManagement: React.FC = () => {
	const [page, setPage] = useState(1);
	const [pageSize] = useState(20);
	const [statusFilter, setStatusFilter] = useState<string | undefined>();
	const [searchQuery, setSearchQuery] = useState("");
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [selectedOrganization, setSelectedOrganization] =
		useState<Organization | null>(null);

	const { data, isLoading, error, refetch } = useListOrganizationsQuery({
		page,
		page_size: pageSize,
		status: statusFilter,
		search: searchQuery || undefined,
	});

	const [deleteOrganization, { isLoading: isDeleting }] =
		useDeleteOrganizationMutation();

	const handleDelete = async (id: string, name: string) => {
		if (
			!window.confirm(
				`Are you sure you want to delete organization "${name}"? This action cannot be undone.`,
			)
		) {
			return;
		}

		try {
			await deleteOrganization(id).unwrap();
			refetch();
		} catch (error: any) {
			console.error("Failed to delete organization:", error);
			alert(
				error?.data?.error ||
					error?.data?.message ||
					"Failed to delete organization. Please try again.",
			);
		}
	};

	const handleEdit = (organization: Organization) => {
		setSelectedOrganization(organization);
		setIsCreateModalOpen(true);
	};

	const handleCreate = () => {
		setSelectedOrganization(null);
		setIsCreateModalOpen(true);
	};

	const handleModalClose = () => {
		setIsCreateModalOpen(false);
		setSelectedOrganization(null);
		refetch();
	};

	if (error) {
		return (
			<div className="w-full">
				<div className="p-4 sm:p-6">
					<div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
						<Icon name="AlertCircle" size={48} className="text-destructive" />
						<h2 className="text-xl font-semibold text-foreground">
							Error Loading Organizations
						</h2>
						<p className="text-muted-foreground">
							Failed to load organizations. Please try again.
						</p>
						<Button onClick={() => refetch()}>Retry</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold text-foreground">
						Organization Management
					</h1>
					<p className="text-muted-foreground mt-1">
						Manage organizations and their settings
					</p>
				</div>
				<Button onClick={handleCreate} iconName="Plus" iconPosition="left">
					Create Organization
				</Button>
			</div>

			{/* Filters */}
			<OrganizationFilters
				statusFilter={statusFilter}
				searchQuery={searchQuery}
				onStatusChange={setStatusFilter}
				onSearchChange={setSearchQuery}
				onClearFilters={() => {
					setStatusFilter(undefined);
					setSearchQuery("");
				}}
			/>

			{/* Table */}
			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<Icon
						name="Loader2"
						size={32}
						className="animate-spin text-primary"
					/>
				</div>
			) : (
				<OrganizationTable
					organizations={data?.results || []}
					onEdit={handleEdit}
					onDelete={handleDelete}
					isDeleting={isDeleting}
					currentPage={page}
					totalCount={data?.count || 0}
					pageSize={pageSize}
					onPageChange={setPage}
				/>
			)}

			{/* Create/Edit Modal */}
			{isCreateModalOpen && (
				<CreateOrganizationModal
					isOpen={isCreateModalOpen}
					onClose={handleModalClose}
					organization={selectedOrganization}
				/>
			)}
		</div>
	);
};

export default OrganizationManagement;
